"use client";

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Code, X, Lock, Settings, Loader2 } from "lucide-react";
import { cn } from "@/modern/utils";
import { HTMLRenderer } from "@/modern/components/ui/html-renderer";
import { useAnalyzeFileContext, type InsightMessage, type Persona } from "./context/AnalyzeFileContext";
import { AnalyzeFileProvider } from "./context/AnalyzeFileContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useHistory } from "react-router-dom";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { StringHelper } from "@bryntum/schedulerpro";
import { OpenAIKeyModal } from "@/react/components/entityViews/settings/components/OpenAIKeyModal";
import { useBoardPermissions } from "@/react/components/entityViews/settings/hooks/useBoardPermissions";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { FormattedFile } from "../../../context/EntityFileContext";
import { EntityRecord } from "@/types/entity";

interface AnalyzeDialogProps {
  entityRecord: EntityRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file?: FormattedFile;
  onAfterCCFileUpload?: (file: FormattedFile) => void;
}

// TypingIndicator Component
const TypingIndicator = ({
  personaName,
  personaColor,
  personaAvatar,
  personaIcon
}: {
  personaName: string;
  personaColor: string;
  personaAvatar?: string | null;
  personaIcon?: React.ComponentType<{ className?: string }>;
}) => {
  const Icon = personaIcon || Code;
  const hasAvatar = personaAvatar;

  return (
    <div className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden", personaColor, !hasAvatar && "text-white")}>
        {hasAvatar ? <img src={personaAvatar} alt={personaName} className="w-full h-full object-cover" /> : <Icon className="w-4 h-4" />}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-foreground mb-1">{personaName}</div>
        <div className="bg-muted rounded-lg px-4 py-3 inline-block">
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// PersonaSidebar Component
const PersonaSidebar = ({
  personas,
  selectedPersonaId,
  onPersonaSelect,
  isLocked
}: {
  personas: Persona[];
  selectedPersonaId: string | null;
  onPersonaSelect: (persona: Persona) => void;
  isLocked: boolean;
}) => {
  const { typingPersonas } = useAnalyzeFileContext();

  return (
    <div className="w-64 border-r border-border bg-muted/30 flex flex-col">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Advisors</h2>
        <p className="text-xs text-muted-foreground mt-1">Select an advisor to analyze</p>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {personas.map((persona) => {
          const Icon = persona.icon;
          const isSelected = selectedPersonaId === persona.id;
          const hasAvatar = persona.avatarUrl;
          const isGenerating = typingPersonas.has(persona.id);
          const isDisabled = isLocked || isGenerating;
          return (
            <button
              key={persona.id}
              onClick={() => !isDisabled && onPersonaSelect(persona)}
              disabled={isDisabled}
              className={cn(
                "w-full text-left p-3 rounded-lg transition-colors mb-2 relative",
                isSelected ? "bg-primary/10 border border-primary/20" : "hover:bg-background/50 border border-transparent",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex items-start gap-3">
                <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden", persona.color, !hasAvatar && "text-white")}>
                  {hasAvatar ? <img src={persona.avatarUrl!} alt={persona.name} className="w-full h-full object-cover" /> : <Icon className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={cn("text-sm font-medium", isSelected ? "text-primary" : "text-foreground")}>
                    {persona.name}
                    {isGenerating && (
                      <Loader2 className="w-3 h-3 ml-2 inline-block animate-spin" />
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{persona.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Grouped Persona Section Component
const PersonaInsightGroup = ({
  persona,
  messages,
  onDeleteMessage
}: {
  persona: Persona;
  messages: InsightMessage[];
  onDeleteMessage: (id: string) => void;
}) => {
  const Icon = persona.icon;
  const hasAvatar = persona.avatarUrl;

  return (
    <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Persona Header */}
      <div className="flex items-center gap-3 pb-2">
        <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden", persona.color, !hasAvatar && "text-white")}>
          {hasAvatar ? <img src={persona.avatarUrl!} alt={persona.name} className="w-full h-full object-cover" /> : <Icon className="w-5 h-5" />}
        </div>
        <div>
          <div className="text-base font-semibold text-foreground">{persona.name}</div>
          <div className="text-xs text-muted-foreground">{persona.description}</div>
        </div>
      </div>

      {/* Insight Cards */}
      <div className="flex flex-col gap-4">
        {messages.map((message, index) => (
          <InsightCard key={message.id} message={message} index={index} onDelete={onDeleteMessage} />
        ))}
      </div>
    </div>
  );
};

// Individual Insight Card (without persona header)
const InsightCard = ({ message, index, onDelete }: { message: InsightMessage; index: number; onDelete: (id: string) => void }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(() => {
    setIsDeleting(true);
    setTimeout(() => {
      onDelete(message.id);
    }, 200);
  }, [message.id, onDelete]);

  return (
    <div
      className={cn("relative", isDeleting && "animate-out fade-out slide-out-to-bottom-2 duration-200")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative p-4 bg-card rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <HTMLRenderer
              content={message.content}
              className="prose-code:bg-muted prose-pre:bg-muted"
            />
            <div className="text-xs text-muted-foreground mt-2">{new Date(message.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        </div>
        {isHovered && (
          <button
            onClick={handleDelete}
            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 transition-colors shadow-sm z-10"
            aria-label="Delete insight"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// ChatArea Component
const ChatArea = ({ messages, isTyping, typingPersona, onDeleteMessage, onClose }: { messages: InsightMessage[]; isTyping: boolean; typingPersona: Persona | null; onDeleteMessage: (id: string) => void; onClose: () => void }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { personas, typingPersonas } = useAnalyzeFileContext();
  const prevMessageCountRef = useRef(messages.length);

  useEffect(() => {
    // Only scroll to bottom if messages were added (not removed) or typing state changed
    const messageCountIncreased = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    if (messageCountIncreased || typingPersonas.size > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, typingPersonas.size]);

  // Get all typing personas (from the set)
  const activeTypingPersonas = useMemo(() => {
    return Array.from(typingPersonas)
      .map((personaId) => personas.find((p) => p.id === personaId))
      .filter((p): p is Persona => p !== undefined);
  }, [typingPersonas, personas]);

  // Group messages by personaId and sort by latest timestamp
  const groupedMessages = useMemo(() => {
    const groups = new Map<string, InsightMessage[]>();

    // Group messages
    messages.forEach((message) => {
      if (!groups.has(message.personaId)) {
        groups.set(message.personaId, []);
      }
      groups.get(message.personaId)!.push(message);
    });

    // Sort messages within each group by timestamp (oldest first)
    groups.forEach((msgs) => {
      msgs.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    });

    // Convert to array and sort groups by latest message timestamp (oldest group first, newest at bottom)
    return Array.from(groups.entries())
      .map(([personaId, msgs]) => ({
        personaId,
        messages: msgs,
        latestTimestamp: Math.max(...msgs.map(m => m.timestamp.getTime()))
      }))
      .sort((a, b) => a.latestTimestamp - b.latestTimestamp);
  }, [messages]);

  return (
    <div className="flex-1 flex flex-col bg-background">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Insights</h2>
          <p className="text-xs text-muted-foreground mt-1">Generated analysis from selected advisors</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close dialog"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-6">
          {groupedMessages.length === 0 && activeTypingPersonas.length === 0 && (
            <div className="flex items-center justify-center h-full text-center">
              <div className="text-muted-foreground">
                <p className="text-sm">No insights yet</p>
                <p className="text-xs mt-1">Select an advisor from the sidebar to generate insights</p>
              </div>
            </div>
          )}

          {/* Render grouped messages */}
          {groupedMessages.map(({ personaId, messages: personaMessages }) => {
            const persona = personas.find((p) => p.id === personaId);
            if (!persona) return null;

            return (
              <PersonaInsightGroup
                key={personaId}
                persona={persona}
                messages={personaMessages}
                onDeleteMessage={onDeleteMessage}
              />
            );
          })}

          {/* Show typing indicators for all active typing personas */}
          {activeTypingPersonas.map((persona) => (
            <TypingIndicator key={persona.id} personaName={persona.name} personaColor={persona.color} personaAvatar={persona.avatarUrl} personaIcon={persona.icon} />
          ))}
          {/* Legacy support: show single typing indicator if still using old state */}
          {isTyping && typingPersona && !typingPersonas.has(typingPersona.id) && (
            <TypingIndicator personaName={typingPersona.name} personaColor={typingPersona.color} personaAvatar={typingPersona.avatarUrl} personaIcon={typingPersona.icon} />
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

// Locked State Component
const LockedState = ({ onNavigateToSettings, onOpenKeyModal: _onOpenKeyModal, canWrite }: { onNavigateToSettings: () => void; onOpenKeyModal: () => void; canWrite: boolean }) => {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">AI Key Required</h3>
          <p className="text-sm text-muted-foreground">An OpenAI API key needs to be added to use AI insights. Configure your API key in Settings to enable AI-powered analysis.</p>
        </div>
        <div className="flex flex-col gap-2">
          {!canWrite && (
            <p className="text-xs text-muted-foreground mb-2">Only the Board Owner can configure the API key.</p>
          )}
          <Button onClick={onNavigateToSettings} variant="outline" className="w-full">
            <Settings className="w-4 h-4 mr-2" />
            Go to Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main AnalyzeDialog Component
const AnalyzeDialogContent = ({ onOpenChange }: { onOpenChange: (open: boolean) => void }) => {
  const { messages, selectedPersonaId, isTyping, typingPersona, personas, handlePersonaSelect, handleDeleteMessage, isLoadingAvatars, isUploadingCCFile } =
    useAnalyzeFileContext();
  const { userPreferences, viewManager, selectedBoard, entityManager } = useSwivuBryntumContext();
  const history = useHistory();
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const { canWrite } = useBoardPermissions();

  // Subscribe to board preferences changes to detect API key updates
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      return record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey;
    }
  });

  // Check if OpenAI API key exists
  const hasOpenAIKey = useMemo(() => {
    if (!userPreferences) return false;
    return userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";
  }, [userPreferences]);

  const handleNavigateToSettings = useCallback(() => {
    if (!selectedBoard?.record) return;
    const id = StringHelper.generateUUID();
    // @ts-expect-error - selectedBoard.record type not fully defined
    const settingsUrl = `/${selectedBoard.record.slug}/settings`;
    history.push(settingsUrl, { viewId: id, viewType: VIEW_TYPES.settings });
    viewManager?.selectViewAnonymousView(id, "Settings", VIEW_TYPES.settings);
    onOpenChange(false);
    if (entityManager?.isOpen) {
      entityManager.closeEntityManager();
    }
  }, [selectedBoard, history, viewManager, entityManager, onOpenChange]);

  const handleOpenKeyModal = useCallback(() => {
    setIsKeyModalOpen(true);
  }, []);

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-1 overflow-hidden relative">
          {/* Loader overlay while avatars are loading */}
          {(isLoadingAvatars || isUploadingCCFile) && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-foreground" />
                <p className="text-sm text-muted-foreground">{isUploadingCCFile ? "Processing image file..." : "Loading advisors..."}</p>
              </div>
            </div>
          )}
          <PersonaSidebar
            personas={personas}
            selectedPersonaId={selectedPersonaId}
            onPersonaSelect={handlePersonaSelect}
            isLocked={!hasOpenAIKey}
          />
          {!hasOpenAIKey ? (
            <LockedState onNavigateToSettings={handleNavigateToSettings} onOpenKeyModal={handleOpenKeyModal} canWrite={canWrite} />
          ) : (
            <ChatArea messages={messages} isTyping={isTyping} typingPersona={typingPersona} onDeleteMessage={handleDeleteMessage} onClose={() => onOpenChange(false)} />
          )}
        </div>
      </div>
      {canWrite && <OpenAIKeyModal isOpen={isKeyModalOpen} onClose={() => setIsKeyModalOpen(false)} />}
    </>
  );
};

export const AnalyzeDialog = ({ open, onOpenChange, file, entityRecord, onAfterCCFileUpload }: AnalyzeDialogProps) => {
  // Create a dummy editor instance for BaseModal compatibility
  const editorInstance = useMemo(() => ({ editorRef: null }), []);

  // Create a dummy record for BaseModal (required prop)
  const dummyRecord = useMemo(() => ({ isPhantom: true }), []);

  const MODAL_SIZE_CONFIG = useMemo(() => ({
    width: "70vw",
    height: "70vh",
    minWidth: "min(70vw, 600px)",
    minHeight: "min(70vh, 500px)",
    maxWidth: "70vw",
    maxHeight: "100vh"
  }), []);

  if (!file) return null;

  // Keep provider mounted to allow cleanup when open changes to false
  return (
    <AnalyzeFileProvider open={open} file={file} entityRecord={entityRecord} onAfterCCFileUpload={onAfterCCFileUpload}>
      {open && (
        <BaseModal
          title="AI Analysis"
          description=""
          onClose={() => onOpenChange(false)}
          size={MODAL_SIZE_CONFIG}
          editorInstance={editorInstance}
          record={dummyRecord}
          headerIconCls=""
          modal={true}
          version=""
          hideMenu={true}
          headerRenderer={() => <></>}
        >
          <AnalyzeDialogContent onOpenChange={onOpenChange} />
        </BaseModal>
      )}
    </AnalyzeFileProvider>
  );
};
